import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useFormikContext } from 'formik';
import { hasUsableValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';
import { toControlValue } from './calculatedControlValue';
import type { ControlOption } from './calculatedControlValue';
import { useCalculatedValue } from './useCalculatedValue';
import type { CalculatedFieldState } from './calculatedField.interface';

export interface CalculatedFieldSyncProps {
  // The form field the result is written to, so the field's own control shows
  // it — a calculated Yes/No is the Yes/No control, not a box of text.
  name: string;
  formula: string;
  fieldNameOf: (token: string) => string;
  // The backend's verdict on the saved rule. Omitted — nothing sends it yet —
  // means show whatever the server computes now.
  state?: CalculatedFieldState;
  // The fields a 'blocked' rule is still waiting on, named for the recruiter.
  blockedBy?: string[];
  // Unlocked, the recruiter may type over the result, so it is theirs to keep.
  locked?: boolean;
  // The field's own Mandatory setting. An optional field that cannot compute
  // yet is simply blank — saying why belongs on a field the filler has to
  // fill. Product's call (Daniel, on the offer form design).
  required?: boolean;
  subjectType?: string;
  kind?: string;
  fieldId?: string | number;
  // A select's own options: the formula answers with their names, the control
  // holds their ids.
  options?: ControlOption[];
  multi?: boolean;
}

const blockedMessage = (fieldName: string) =>
  `Fill in the dependent "${fieldName}" field to see this value`;

const Refusal = ({ children }: { children: string }) => (
  // kula Text drops data-* attributes, so the hook sits on the wrapper.
  <Box mt="1" data-test-id="calculated-blocked">
    <Text variant="small" style={{ color: colors['pastel-1-dark'] }}>
      {children}
    </Text>
  </Box>
);

// Sits under a calculated field: works the value out, puts it where the
// field's own control reads it, and says why when there is none to show.
// It renders no control of its own — the kind's config already does.
export const CalculatedFieldSync = ({
  name,
  formula,
  fieldNameOf,
  state,
  blockedBy,
  locked = true,
  required = false,
  subjectType,
  kind,
  fieldId,
  options,
  multi,
}: CalculatedFieldSyncProps) => {
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const { value: computed, missing } = useCalculatedValue({
    formula,
    values,
    fieldNameOf,
    subjectType,
    kind,
    fieldId,
  });

  const waitingFor = blockedBy?.[0] ?? missing[0]?.label;
  const blocked = state === 'blocked' || (!state && Boolean(waitingFor));
  // A 'blocked' or 'error' rule can still carry a value from an earlier save,
  // so the state decides whether a value may be shown at all.
  const usable = hasUsableValue({
    state: blocked ? 'blocked' : (state ?? 'ok'),
    value: computed,
  });

  const shown = usable
    ? toControlValue(kind, computed, { options, multi })
    : { value: '' };
  const next = shown.unstorable ? '' : shown.value;

  // Only a locked field publishes: an unlocked one would read its own result
  // back as an override and stop following its inputs, which is the opposite
  // of what a formula is for.
  useEffect(() => {
    if (!locked) return;
    if (JSON.stringify(values[name] ?? '') === JSON.stringify(next ?? ''))
      return;
    setFieldValue(name, next);
  }, [locked, name, next, setFieldValue, values]);

  if (shown.unstorable) return <Refusal>{shown.unstorable}</Refusal>;
  if (blocked && waitingFor && required)
    return <Refusal>{blockedMessage(waitingFor)}</Refusal>;
  return null;
};
